import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, message } from 'antd';
import { CheckCircleOutlined, UploadOutlined } from '@ant-design/icons';

import ModalComponent from '../../../../../../components/Modal';
import { anexarDocumentoConsulta } from '../../../../../../services/documento';
import * as S from './styles';

export function AnexarDocumentoConsulta({
  idConsultaSolicitacao,
  idIntegracaoFornecedor,
  onDocumentoAnexado,
}) {
  const inputArquivoRef = useRef(null);
  const areaColarRef = useRef(null);
  const [modalVisivel, setModalVisivel] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!modalVisivel) return undefined;

    const timeoutFoco = setTimeout(() => areaColarRef.current?.focus(), 0);
    return () => clearTimeout(timeoutFoco);
  }, [modalVisivel]);

  useEffect(() => {
    if (!arquivo?.type?.startsWith('image/')) {
      setPreview(null);
      return undefined;
    }

    const urlPreview = URL.createObjectURL(arquivo);
    setPreview(urlPreview);

    return () => URL.revokeObjectURL(urlPreview);
  }, [arquivo]);

  const fecharModal = () => {
    setArquivo(null);
    setModalVisivel(false);
  };

  const handleSelecionarArquivo = event => {
    const inputArquivo = event.target;
    const arquivoSelecionado = inputArquivo.files?.[0];

    if (arquivoSelecionado) setArquivo(arquivoSelecionado);
    inputArquivo.value = '';
  };

  const handleColarImagem = event => {
    event.preventDefault();

    const itens = Array.from(event.clipboardData?.items || []);
    const itemImagem = itens.find(item => item.type.startsWith('image/'));
    const imagem = itemImagem?.getAsFile();

    if (!imagem) {
      message.error('A área de transferência não possui uma imagem.');
      return;
    }

    setArquivo(imagem);
  };

  const handleEnviarArquivo = async () => {
    if (!arquivo) return;

    setEnviando(true);

    try {
      const response = await anexarDocumentoConsulta(
        idConsultaSolicitacao,
        arquivo
      );
      onDocumentoAnexado(response.data);
      message.success('Arquivo anexado com sucesso.');
      fecharModal();
    } catch (error) {
      const detalheErro = error?.response?.data;
      message.error(
        detalheErro?.detail ||
          (typeof detalheErro === 'string' && detalheErro) ||
          'Não foi possível anexar o arquivo.'
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <S.AcoesAnexo>
        {idIntegracaoFornecedor && (
          <S.DocumentoAnexado>
            <CheckCircleOutlined aria-label="Documento anexado" />
            <span>Imagem/documento anexado</span>
          </S.DocumentoAnexado>
        )}

        <Button
          type="primary"
          size="small"
          icon={<UploadOutlined />}
          onClick={() => setModalVisivel(true)}
          style={{
            background: '#24A95D',
            border: 'none',
            fontWeight: 'bold',
            borderRadius: '0px',
          }}
        >
          Anexar arquivo
        </Button>
      </S.AcoesAnexo>

      <ModalComponent
        title="Anexar arquivo à consulta"
        visible={modalVisivel}
        okText="Enviar"
        onOkFunction={handleEnviarArquivo}
        onCancelFunction={fecharModal}
        okButtonProps={{ disabled: !arquivo, loading: enviando }}
        cancelButtonProps={{ disabled: enviando }}
      >
        <S.Instrucoes>
          Cole uma imagem na área abaixo ou selecione um arquivo no computador.
        </S.Instrucoes>

        <S.AreaColar
          ref={areaColarRef}
          role="button"
          tabIndex={0}
          onPaste={handleColarImagem}
          aria-label="Colar imagem da área de transferência"
        >
          {preview ? (
            <S.Preview
              src={preview}
              alt="Pré-visualização da imagem selecionada"
            />
          ) : (
            <>
              <UploadOutlined />
              <span>Clique nesta área e pressione Ctrl+V para colar</span>
            </>
          )}
        </S.AreaColar>

        {arquivo && <S.NomeArquivo>{arquivo.name}</S.NomeArquivo>}

        <input
          ref={inputArquivoRef}
          type="file"
          hidden
          onChange={handleSelecionarArquivo}
        />
        <Button onClick={() => inputArquivoRef.current?.click()}>
          Selecionar arquivo
        </Button>
      </ModalComponent>
    </>
  );
}

AnexarDocumentoConsulta.propTypes = {
  idConsultaSolicitacao: PropTypes.number.isRequired,
  idIntegracaoFornecedor: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  onDocumentoAnexado: PropTypes.func.isRequired,
};

AnexarDocumentoConsulta.defaultProps = {
  idIntegracaoFornecedor: null,
};
